import { Location } from 'history'
import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import jwt from 'jsonwebtoken'
import { useSearchParams } from '../../utils/hooks'

const MetabaseIframe = styled.iframe`
    border: 0;
`

const METABASE_PREFIX = 'metabase:'
const METABASE_SEPARATOR = ','

const METABASE_SITE_URL = 'https://metabase.volumenetwork.com'

type MetabaseSecret = string
type MetabaseDashboard = number
export type MetabaseToken = `${typeof METABASE_PREFIX}:${MetabaseSecret}${typeof METABASE_SEPARATOR}${MetabaseDashboard}`

type MetabaseMessageData<T extends string, D extends Record<string, unknown>> = { type: T } & D

interface MetabasePostMessage {
    metabase: MetabaseMessageData<'location', { location: Location }>
        | MetabaseMessageData<string, Record<string, unknown>>
}

function Metabase({ token, name }: { token: MetabaseToken, name: string }) {
    const [searchParams, setSearchParams] = useSearchParams({
        date_start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        date_end: moment().format('YYYY-MM-DD'),
    })

    React.useEffect(() => {
        // Metabase sends postMessages everytime that it's location changes.
        // The information in those messages can be used to persist the value for the filters
        const updateFilterParams = (event: MessageEvent<MetabasePostMessage>) => {
            // Check that the message is coming from Metabase
            if (event.origin !== METABASE_SITE_URL) {
                return
            }

            // Discard any message different to the location one
            if (event.data.metabase.type !== 'location') {
                return
            }

            // Get the payload from the message
            const metabaseData = event.data.metabase as MetabaseMessageData<'location', { location: Location }>

            // Update app's URL with the Metabase search parameters
            setSearchParams(metabaseData.location.search)
        }
        
        window.addEventListener('message', updateFilterParams)

        return () => {
            window.removeEventListener('message', updateFilterParams)
        }
    }, [setSearchParams])

    // Build the URL for the Metabase iframe
    const src = React.useMemo(() => {
        // The dashboard token comes with the secret key and the number of the Metabase dashboard
        const [METABASE_SECRET_KEY, dashboard] = token.replace(METABASE_PREFIX, '').split(METABASE_SEPARATOR)

        // Build the payload to be encrypted
        const payload = {
            resource: { dashboard: parseInt(dashboard) },
            params: {},
            exp: Math.round(Date.now() / 1000) + (10 * 60) // 10 minute expiration,
        }

        // Encrypt the payload
        const signature = jwt.sign(payload, METABASE_SECRET_KEY)

        // Build an URL object
        const url = new URL(METABASE_SITE_URL)

        // Update the path for the dashboard URL
        url.pathname = '/embed/dashboard/' + signature

        // Use search params from parent's URL
        searchParams.forEach((value, key) => {
            url.searchParams.set(key, value)
        })

        // Build the full URL for the iframe
        return url.toString()
    }, [token, searchParams])

    return (
        <MetabaseIframe
            title={name}
            width="100%"
            height="100%"
            src={src}
            allowFullScreen
        />
    )
}

Metabase.isMetabase = (id: string) => id.startsWith(METABASE_PREFIX)

export default Metabase
